import React, { useEffect, useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { LoadingState } from '@/components/ui/LoadingState';
import { ErrorState } from '@/components/ui/ErrorState';
import { attachmentsApi } from '@/api/shopify.api';
import { ApiError } from '@/lib/errors';
import { formatBytes } from '@/lib/format';

/**
 * V4.9.7 — Inline preview for ticket attachments. Opens in a modal instead
 * of forcing a download.
 *
 * Supported preview types (covers ~95% of real-world attachments):
 *   • Images (PNG, JPG, JPEG, GIF, WebP, SVG, BMP) — rendered via <img>
 *   • PDFs — rendered via <iframe> using the browser's native viewer
 *   • Text (TXT, CSV, JSON, MD, LOG, XML) — fetched as text, shown in <pre>
 *
 * Fallback for unsupported types (DOCX, XLSX, PPTX, ZIP, video, audio …):
 *   Shows a friendly message + a "Download instead" button.
 */

interface Props {
  open: boolean;
  onClose: () => void;
  attachment: {
    id: string;
    s3Key: string;
    filename: string;
    mimeType: string | null;
    sizeBytes: string | null;
  } | null;
  onDownload: () => void;
}

type Kind = 'image' | 'pdf' | 'text' | 'video' | 'audio' | 'other';

function detectKind(filename: string, mimeType: string | null): Kind {
  const ext = filename.toLowerCase().split('.').pop() ?? '';
  const mt = (mimeType ?? '').toLowerCase();
  if (mt.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico'].includes(ext)) {
    return 'image';
  }
  if (mt === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (mt.startsWith('video/') || ['mp4', 'webm', 'mov', 'm4v'].includes(ext)) return 'video';
  if (mt.startsWith('audio/') || ['mp3', 'wav', 'ogg', 'm4a'].includes(ext)) return 'audio';
  if (
    mt.startsWith('text/') ||
    mt === 'application/json' ||
    mt === 'application/xml' ||
    ['txt', 'csv', 'json', 'md', 'log', 'xml', 'yaml', 'yml', 'tsv'].includes(ext)
  ) {
    return 'text';
  }
  return 'other';
}

export function AttachmentPreviewModal({ open, onClose, attachment, onDownload }: Props) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const kind = attachment ? detectKind(attachment.filename, attachment.mimeType) : 'other';

  // Load the blob when modal opens for a previewable kind. Revoke on close
  // / unmount / next preview so we don't leak memory.
  useEffect(() => {
    if (!open || !attachment) {
      return;
    }
    if (kind === 'other') {
      setBlobUrl(null);
      setTextContent(null);
      return;
    }
    setLoading(true);
    setError(null);
    setTextContent(null);
    let revokedUrl: string | null = null;
    attachmentsApi
      .view(attachment.s3Key)
      .then(async ({ blobUrl: url }) => {
        revokedUrl = url;
        setBlobUrl(url);
        if (kind === 'text') {
          // For text we need the actual string content, not just the URL.
          // Fetching from the local blob URL is fast and synchronous-ish.
          try {
            const resp = await fetch(url);
            const txt = await resp.text();
            // Cap at 200KB so the modal doesn't lock the tab on huge logs.
            setTextContent(txt.length > 200_000 ? txt.slice(0, 200_000) + '\n…[truncated]' : txt);
          } catch (e) {
            setError('Could not decode text content.');
          }
        }
      })
      .catch((err) => {
        setError(
          err instanceof ApiError
            ? err.problem?.detail ?? err.message
            : 'Could not load preview.'
        );
      })
      .finally(() => setLoading(false));
    return () => {
      if (revokedUrl) {
        URL.revokeObjectURL(revokedUrl);
      }
      setBlobUrl(null);
      setTextContent(null);
    };
  }, [open, attachment?.s3Key, kind, attachment]);

  if (!attachment) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={attachment.filename}
      size="lg"
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          <button type="button" className="btn btn-primary" onClick={onDownload}>
            Download
          </button>
        </>
      }
    >
      <div className="attachment-preview">
        <div className="attachment-preview-meta text-small text-muted">
          {attachment.mimeType ?? 'unknown type'} · {formatBytes(attachment.sizeBytes)}
        </div>

        {loading ? <LoadingState /> : null}
        {error ? <ErrorState error={new Error(error)} /> : null}

        {!loading && !error && kind === 'image' && blobUrl ? (
          <div className="attachment-preview-image-wrap">
            <img
              src={blobUrl}
              alt={attachment.filename}
              className="attachment-preview-image"
            />
          </div>
        ) : null}

        {!loading && !error && kind === 'pdf' && blobUrl ? (
          <iframe
            src={blobUrl}
            title={attachment.filename}
            className="attachment-preview-pdf"
          />
        ) : null}

        {!loading && !error && kind === 'video' && blobUrl ? (
          <video src={blobUrl} controls className="attachment-preview-video" />
        ) : null}

        {!loading && !error && kind === 'audio' && blobUrl ? (
          <audio src={blobUrl} controls className="attachment-preview-audio" />
        ) : null}

        {!loading && !error && kind === 'text' && textContent !== null ? (
          <pre className="attachment-preview-text">{textContent}</pre>
        ) : null}

        {!loading && !error && kind === 'other' ? (
          <div className="state">
            <h4>Preview not supported</h4>
            <p className="text-small">
              This file type can't be displayed in the browser. Download it to view.
            </p>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
